import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';

import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';

// Mirrors ticketing-library ClaimTicketResult.
type ClaimTicketResult = {
  result: 'CLAIMED' | 'ALREADY_OWNER' | 'CLAIMED_BY_OTHER';
  owners: string[];
  ticket: TicketQueryResult;
};

type ClaimState =
  | { status: 'claiming' }
  | { status: 'already-claimed'; ticketNumber: string; ownerName: string }
  | { status: 'error'; message: string };

const ClaimTicketPage = () => {
  const { id: ticketId } = useParams();
  const navigate = useNavigate();
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [claimState, setClaimState] = useState<ClaimState>({
    status: 'claiming',
  });
  // Guards against a double POST (StrictMode double-invoke, context re-renders).
  const hasStarted = useRef(false);

  const ticketUrl = `/dashboard/ticket/${ticketId}`;

  useEffect(() => {
    if (hasStarted.current || !isNotNull(userProfile) || !isNotNull(ticketId)) {
      return;
    }
    hasStarted.current = true;

    // The API adds the Assignee, releases the escalation queue assignment and notifies the queue.
    fetch(`${TICKET_ENDPOINT}/${ticketId}/claim`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Unable to claim ticket (${res.status}).`);
        }
        return res.json();
      })
      .then((claim: ClaimTicketResult) => {
        if (claim.result === 'CLAIMED_BY_OTHER') {
          setClaimState({
            status: 'already-claimed',
            ticketNumber: claim.ticket?.ticket?.number ?? '',
            ownerName: (claim.owners ?? []).join(', '),
          });
          return;
        }
        navigate(ticketUrl, { replace: true });
      })
      .catch((err: Error) => {
        setClaimState({ status: 'error', message: err.message });
      });
  }, [ticketId, userProfile]);

  const renderContent = () => {
    if (claimState.status === 'already-claimed') {
      return (
        <Result
          status="info"
          title={`Ticket ${claimState.ticketNumber} has already been claimed`}
          subTitle={`Currently assigned to: ${claimState.ownerName}`}
          extra={
            <Button type="primary" onClick={() => navigate(ticketUrl)}>
              View Ticket
            </Button>
          }
        />
      );
    }

    if (claimState.status === 'error') {
      return (
        <Result
          status="error"
          title="Unable to claim ticket"
          subTitle={claimState.message}
          extra={
            <Button type="primary" onClick={() => navigate(ticketUrl)}>
              View Ticket
            </Button>
          }
        />
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" tip="Claiming ticket...">
          <div style={{ height: 80 }} />
        </Spin>
      </div>
    );
  };

  return (
    <ContentLayout>
      <IBCard isCovered title="Claim Ticket">
        {renderContent()}
      </IBCard>
    </ContentLayout>
  );
};

export default ClaimTicketPage;
